import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useFetchLostPets from '../../hooks/useFetchLostPets';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, InfoWindow, Autocomplete } from '@react-google-maps/api';
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import petSpecies from '../../data/petSpecies';
import '../../style/LostPetsPage.css';

const LostPetsPage = ({ isLoaded }) => {
    const { t, i18n } = useTranslation();
    const { lostPets, loading, error } = useFetchLostPets();
    const navigate = useNavigate();
    const [view, setView] = useState('list');
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [selectedPet, setSelectedPet] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [selectedPetType, setSelectedPetType] = useState('');
    const autocompleteRef = useRef(null);

    const storedUser = JSON.parse(localStorage.getItem('user'));
    let userId;
    if (storedUser?.token) {
        const decoded = jwtDecode(storedUser.token);
        userId = decoded.id || decoded._id || decoded.googleId || decoded.email;
    }

    const handlePlaceChanged = () => {
        const autocomplete = autocompleteRef.current;
        if (!autocomplete) return;

        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
            setSelectedLocation({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            });
        }
    };

    if (loading) return <p className="text-dafauna-gray">{t('lostPetsPage.loading')}</p>;
    if (error) return <p className="text-red-500">{t('lostPetsPage.errorFetching')}</p>;

    const dateRanges = [
        { label: t('lostPetsPage.lastDay'), value: 1 },
        { label: t('lostPetsPage.last10Days'), value: 10 },
        { label: t('lostPetsPage.lastMonth'), value: 30 },
        { label: t('lostPetsPage.lastQuarter'), value: 90 },
        { label: t('lostPetsPage.lastYear'), value: 365 },
    ];

    const isEligible = async (petOrganizationId, userId) => {
        try {
            console.log('Checking eligibility for petOrganizationId:', petOrganizationId, 'and userId:', userId); // Debugging log

            if (!petOrganizationId) {
                console.warn('Pet organizationId is missing');
                return false;
            }

            // Fetch the pet's organization details
            const petOrganization = await fetch(`/api/organizations/${petOrganizationId}`).then(res => res.json());
            console.log('Fetched pet organization:', petOrganization);

            if (!petOrganization || !petOrganization.members) {
                console.warn('Invalid or missing pet organization data:', petOrganization);
                return false;
            }

            // Fetch the user's organizations (assuming an API endpoint exists)
            const userOrganizations = await fetch(`/api/users/${userId}/organizations`).then(res => res.json());
            console.log('Fetched user organizations:', userOrganizations);

            if (!Array.isArray(userOrganizations)) {
                console.warn('Invalid or missing user organizations data:', userOrganizations);
                return false;
            }

            // Check if the user belongs to the pet's organization with the required role
            return petOrganization.members.some(
                (member) =>
                    member.userId === userId &&
                    ['admin', 'owner'].includes(member.role) &&
                    userOrganizations.some(org => org.id === petOrganizationId)
            );
        } catch (error) {
            console.error('Error checking eligibility:', error);
            return false;
        }
    };


    const getTranslatedLabel = (options, value) => {
        console.log('Options:', options);
        console.log('Value:', value);
        const option = options.find((o) => o.value === value);
        console.log('Found Option:', option);
        return option ? option.label[i18n.language] : t('general.unknown'); // Use translation for "unknown"
    };

    return (
        <div className="bg-dafauna-background p-section rounded-xl shadow-md max-w-screen-lg mx-auto">
            <h1 className="text-dafauna-darkText text-2xl font-bold text-center mb-section">{t('lostPetsPage.title')}</h1>

            <div className="flex justify-between items-center mb-section">
                <button
                    className="flex items-center gap-2 bg-dafauna-primary text-white py-2 px-4 rounded-md"
                    onClick={() => setFiltersVisible(!filtersVisible)}
                >
                    <HiAdjustmentsHorizontal size={20} /> {filtersVisible ? t('lostPetsPage.hideFilters') : t('lostPetsPage.showFilters')}
                </button>

                <button
                    onClick={() => {
                        setSelectedLocation(null);
                        setSelectedDateRange(null);
                        setSelectedPetType('');
                        if (autocompleteRef.current) autocompleteRef.current.value = '';
                    }}
                    className="bg-red-500 text-white py-2 px-4 rounded-md"
                >
                    {t('lostPetsPage.clearFilters')}
                </button>
            </div>

            {filtersVisible && (
                <div className="flex flex-wrap gap-cardGap mb-section">
                    <Autocomplete onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)} onPlaceChanged={handlePlaceChanged}>
                        <input
                            type="text"
                            placeholder={t('lostPetsPage.searchLocationPlaceholder')}
                            onChange={(e) => {
                                if (!e.target.value) setSelectedLocation(null);
                            }}
                            className="w-full p-2 rounded-md border border-gray-300"
                        />
                    </Autocomplete>

                    <div className="flex items-center gap-2">
                        <label className="text-dafauna-darkText font-medium">{t('lostPetsPage.dateRange')}</label>
                        <select
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                setSelectedDateRange(value);
                            }}
                            className="p-2 rounded-md border border-gray-300"
                        >
                            <option value="">{t('lostPetsPage.selectDateRange')}</option>
                            {dateRanges.map((range) => (
                                <option key={range.value} value={range.value}>{range.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-dafauna-darkText font-medium">{t('lostPetsPage.petType')}</label>
                        <select
                            onChange={(e) => {
                                const value = e.target.value;
                                setSelectedPetType(value);
                            }}
                            className="p-2 rounded-md border border-gray-300"
                        >
                            <option value="">{t('lostPetsPage.selectPetType')}</option>
                            {petSpecies.map((species) => (
                                <option key={species.value} value={species.value}>
                                    {getTranslatedLabel(petSpecies, species.value)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <div className="view-toggle-list-map-floating">
                {view === 'list' && (
                    <button
                        onClick={() => setView('map')}
                        className="py-2 px-4 rounded-md bg-dafauna-darkText text-white shadow-lg"
                    >
                        {t('lostPetsPage.mapView')}
                    </button>
                )}
                {view === 'map' && (
                    <button
                        onClick={() => setView('list')}
                        className="py-2 px-4 rounded-md bg-dafauna-darkText text-white shadow-lg"
                    >
                        {t('lostPetsPage.listView')}
                    </button>
                )}
            </div>

            {view === 'list' && (
                <div className="lost-pets-list">
                    {lostPets.filter(pet => {
                        console.log('Filtering Pet:', pet.name, 'Selected Date Range:', selectedDateRange, 'Selected Pet Type:', selectedPetType); // Debugging log

                        if (!selectedLocation && !selectedDateRange && !selectedPetType) return true; // Show all pets if no filters are applied

                        const petLocation = pet.location;

                        // Location filter
                        if (selectedLocation) {
                            if (!petLocation) return false;
                            const distance = Math.sqrt(
                                Math.pow(petLocation.lat - selectedLocation.lat, 2) +
                                Math.pow(petLocation.lng - selectedLocation.lng, 2)
                            );
                            if (distance >= 0.1) return false; // Example threshold for filtering by proximity
                        }

                        // Date range filter
                        if (selectedDateRange) {
                            if (!pet.lostInfo || !pet.lostInfo.lastSeenDate) {
                                console.warn('Missing lastSeenDate for pet:', pet.name); // Log missing date
                                return false; // Exclude pets with missing lastSeenDate when a date range is selected
                            }

                            const petDate = new Date(pet.lostInfo.lastSeenDate);
                            if (isNaN(petDate)) {
                                console.warn('Invalid lastSeenDate for pet:', pet.name, 'Value:', pet.lostInfo.lastSeenDate); // Log invalid date
                                return false;
                            }

                            const daysAgo = new Date();
                            daysAgo.setDate(daysAgo.getDate() - selectedDateRange);
                            console.log('Pet Date:', petDate, 'Days Ago:', daysAgo); // Debugging log
                            if (petDate < daysAgo) return false;
                        } else if (!pet.lostInfo || !pet.lostInfo.lastSeenDate) {
                            // Include pets with missing lastSeenDate if no date range is selected
                            return true;
                        }

                        // Pet type filter
                        if (selectedPetType && pet.petType.toLowerCase() !== selectedPetType.toLowerCase()) {
                            console.warn('Pet type mismatch for pet:', pet.name, 'Expected:', selectedPetType, 'Actual:', pet.petType); // Log mismatch
                            return false;
                        }

                        return true;
                    }).map((pet) => {
                        const isOwner = pet.owner && String(pet.owner) === String(userId);
                        console.log('pet.owner:', pet.owner, 'userId:', userId, 'isOwner:', isOwner);

                        // Calculate days since lost
                        let daysSinceLost = null;
                        if (pet.lostInfo && pet.lostInfo.lastSeenDate) {
                            const lastSeenDate = new Date(pet.lostInfo.lastSeenDate);
                            if (!isNaN(lastSeenDate)) {
                                const currentDate = new Date();
                                const timeDiff = currentDate - lastSeenDate;
                                daysSinceLost = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                            }
                        }

                        // Add debugging log for the entire pet object when organizationId is missing
                        if (!pet.organizationId) {
                            console.warn('Missing organizationId for pet:', pet);
                        }

                        return (
                            <div key={pet.id} className="pet-card-lost">
                                <div className="pet-card-left">
                                    {Array.isArray(pet.images) && pet.images.length > 0 ? (
                                        <img
                                            src={
                                                pet.images.find(image => image.tag === 'profile')?.url || pet.images[0]
                                            }
                                            alt={pet.name}
                                        />
                                    ) : (
                                        <p className="placeholder-image">{t('generalPet.imageAvailable')}</p>
                                    )}
                                </div>
                                <div className="pet-card-right">
                                    <div className="pet-info">
                                        <h2>{pet.name}</h2>
                                        <p>-</p>
                                        <p>{getTranslatedLabel(petSpecies, pet.petType) || t('generalPet.unknown')}</p>
                                    </div>
                                    {daysSinceLost !== null && (
                                        <p>{t('lostPetsPage.daysLost', { count: daysSinceLost })}</p>
                                    )}
                                    {isOwner ? (
                                        pet.organizationId ? (
                                            isEligible(pet.organizationId, userId) ? (
                                                <button onClick={() => navigate(`/pet/${pet.id}`)}>{t('lostPetsPage.viewMyPet')}</button>
                                            ) : (
                                                <p>{t('lostPetsPage.accessDenied')}</p>
                                            )
                                        ) : (
                                            <p>{t('lostPetsPage.organizationMissing')}</p> // Show a message if organizationId is missing
                                        )
                                    ) : (
                                        <Link to={`/lost-pet/${pet.id}`}>{t('lostPetsPage.viewDetails')}</Link>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {view === 'map' && (
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '500px' }}
                    center={selectedLocation || { lat: 41.3851, lng: 2.1734 }}
                    zoom={selectedLocation ? 15 : 10}
                >
                    {lostPets.filter(pet => {
                        // Apply the same filtering logic as the list view
                        if (!selectedLocation && !selectedDateRange && !selectedPetType) return true;

                        const petLocation = pet.location;
                        if (selectedLocation) {
                            if (!petLocation) return false;
                            const distance = Math.sqrt(
                                Math.pow(petLocation.lat - selectedLocation.lat, 2) +
                                Math.pow(petLocation.lng - selectedLocation.lng, 2)
                            );
                            if (distance >= 0.1) return false;
                        }

                        if (selectedDateRange) {
                            if (!pet.lostInfo || !pet.lostInfo.lastSeenDate) return false;

                            const petDate = new Date(pet.lostInfo.lastSeenDate);
                            if (isNaN(petDate)) return false;

                            const daysAgo = new Date();
                            daysAgo.setDate(daysAgo.getDate() - selectedDateRange);
                            if (petDate < daysAgo) return false;
                        }

                        // Pet type filter
                        if (selectedPetType && pet.petType !== selectedPetType) {
                            return false;
                        }

                        return true;
                    }).map((pet) => {
                        const lastSeenLocation = pet.location;

                        if (!lastSeenLocation || !lastSeenLocation.lat || !lastSeenLocation.lng) {
                            console.warn('Invalid lastSeenLocation for pet:', pet, 'Reason: Missing or invalid coordinates');
                            return null;
                        }

                        return (
                            <Marker
                                key={pet.id}
                                position={{ lat: lastSeenLocation.lat, lng: lastSeenLocation.lng }}
                                onClick={() => setSelectedPet(pet)}
                            >
                                {selectedPet?.id === pet.id && (
                                    <InfoWindow
                                        position={{
                                            lat: lastSeenLocation.lat,
                                            lng: lastSeenLocation.lng,
                                        }}
                                        onCloseClick={() => setSelectedPet(null)}
                                    >
                                        <div style={{ textAlign: 'center' }}>
                                            <img
                                                src={
                                                    pet.images?.find(image => image.tag === 'profile')?.url || pet.images?.[0]?.url || ''
                                                }
                                                alt={pet.name}
                                                style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '5px' }}
                                            />
                                            <h2 style={{ fontSize: '16px', margin: '10px 0' }}>{pet.name}</h2>
                                            <Link
                                                to={`/lost-pet/${pet.id}`}
                                                style={{
                                                    display: 'inline-block',
                                                    marginTop: '10px',
                                                    color: '#007bff',
                                                    textDecoration: 'none',
                                                }}
                                            >
                                                View Details
                                            </Link>
                                        </div>
                                    </InfoWindow>
                                )}
                            </Marker>
                        );
                    })}
                </GoogleMap>
            )}
        </div>
    );
};

export default LostPetsPage;